import { TableCell, TableRow } from "flowbite-react";
import { Loader2 } from "lucide-react";
import { Expense } from "@/features/vouchers";
import { useGroupedExpenses } from "@/features/vouchers/hooks/useGroupedExpenses";
import { EuroFormat } from "@/shared/components/euroFormat";
import { H4 } from "@/shared/ui/typographies/h4";
import { formatEuro } from "@/shared/utils/formatEuro";

type ExpenseDetailsPanelProps = {
  expense: Expense
  isVisible: boolean
};

export const ExpenseDetailsPanel = ({ expense, isVisible }: ExpenseDetailsPanelProps) => {
  const groupedExpensesData = useGroupedExpenses({
    payment_id: expense.payment_id,
    enabled: isVisible,
  });

  return (
    <TableRow className="bg-gray-50 dark:bg-gray-700">
      <TableCell colSpan={4} className="p-0 hover:bg-gray-50 dark:hover:bg-gray-700">
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isVisible ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                  Informations de transaction
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Payment ID:</span>
                    <span className="font-mono">{expense.payment_id}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                  Informations marchand
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Marchand ID:</span>
                    <span className="font-mono">{expense.merchant_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Code Marchand:</span>
                    <span className="font-mono">{expense.merchant_code}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                  Informations transaction
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Montant total de la transaction:</span>
                    <span className="font-mono"><EuroFormat amount={expense.total_transaction_amount} /></span>
                  </div>
                </div>
              </div>
            </div>

            {groupedExpensesData.isLoading && (
              <div className="mt-6 text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  {" "}
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {" "}
                  Chargement des détails...
                </div>
              </div>
            )}

            {groupedExpensesData.error && (
              <div className="mt-6 p-3 bg-red-100 dark:bg-red-900 rounded">
                <div className="text-sm text-red-700 dark:text-red-300">
                  Erreur lors du chargement des détails
                </div>
              </div>
            )}

            {groupedExpensesData.data?.expenses && groupedExpensesData.data.expenses.length > 0 && (
              <div className="mt-8">
                <H4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Répartition des bons</H4>

                <div className="space-y-2">
                  {groupedExpensesData.data.expenses[0].transactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex justify-between items-center py-2 px-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {transaction.voucher_employee_voucher_campaign_name}
                      </span>
                      <span className={`font-mono font-medium ${transaction.amount < 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                        {transaction.amount < 0 ? `+${formatEuro(Math.abs(transaction.amount))}` : `-${formatEuro(transaction.amount)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};
